/**
 * Collected result rows for a finished screening run.
 *
 * `reasoning_*` columns are hidden: a reviewer wants the answer before the
 * justification, and the reasoning text is long enough to make the table unusable.
 */

import type { ScreeningRunResults } from "../../api/clinicalTrialsApiTypes";

const TONE: Record<string, string> = {
  PASS: "bg-green-100 text-green-800",
  FAIL: "bg-red-100 text-red-800",
  NEEDS_REVIEW: "bg-amber-100 text-amber-800",
  ACTION_REQUIRED: "bg-amber-100 text-amber-800",
  DATA_MISSING: "bg-amber-100 text-amber-800",
};

/**
 * Render one cell, badging the verdict-ish columns.
 *
 * @param column - Column name
 * @param value - Cell value
 * @returns The cell contents
 */
function cell(column: string, value: unknown) {
  const text = value === null || value === undefined ? "" : String(value);
  const tone = /^(verdict|answer_)/.test(column) ? TONE[text] : undefined;
  if (!tone) return text;
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${tone}`}
    >
      {text}
    </span>
  );
}

interface Props {
  results: ScreeningRunResults;
}

/**
 * The results table.
 *
 * @param props - The run's collected results
 * @returns The results table
 */
export function RunResultsTable({ results }: Props) {
  const columns = results.columns.filter(
    (column) => !column.startsWith("reasoning_"),
  );
  return (
    <div>
      {results.result_path && (
        <p className="text-xs text-gray-500 mb-2 font-mono break-all">
          {results.result_path}
        </p>
      )}
      <div className="overflow-auto rounded-md border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-3 py-2 text-left font-medium text-gray-600"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.rows.map((row, index) => (
              <tr key={index} className="border-t border-gray-100">
                {columns.map((column) => (
                  <td key={column} className="px-3 py-2 align-top">
                    {cell(column, row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
